// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import { ApiHandlerDiscreteParameter, createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import {
    validateAddress,
    ValidateAddressApiParameter,
    ValidateAddressResult,
} from "@mcmc/App/Services/AccountCreationService";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";

export interface ValidateAddressParameter {
    onError?: (error: any) => void;
    onSuccess?: (results: ValidateAddressResult) => void;
}

type HandlerType = ApiHandlerDiscreteParameter<
    ValidateAddressParameter,
    ValidateAddressApiParameter,
    ValidateAddressResult
>;

export const BegintValidateOverrideAddress: HandlerType = props => {
    props.dispatch({
        type: "Pages/CheckoutShipping/BeginUpdateCart",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    const state = props.getState() as McMcApplicationState;
    const address = state.xCustomCheckoutShipping.overrideAddressFormState!.address;
    props.apiParameter = {
        address1: address.address1,
        address2: address.address2,
        city: address.city,
        postalCode: address.postalCode,
        state: address.state!.abbreviation,
    };
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.apiResult = await validateAddress(props.apiParameter);
        if (props.apiResult) {
            props.apiResult.normalizedAddresses = props.apiResult.normalizedAddresses.filter(address => {
                if (
                    address.address1 !== props.apiParameter.address1 ||
                    address.address2 !== props.apiParameter.address2 ||
                    address.city !== props.apiParameter.city ||
                    address.postalCode !== props.apiParameter.postalCode ||
                    address.state !== props.apiParameter.state
                ) {
                    address.id = newGuid();
                    return true;
                }
                return false;
            });
        }
        props.parameter.onSuccess?.(props.apiResult);
    } catch (error) {
        props.parameter.onError?.(
            error && ((error.errorJson && error.errorJson.message) || error.errorMessage || error),
        );
        props.dispatch({
            type: "Pages/CheckoutShipping/CompleteUpdateCart",
        });
        return false;
    }
};

export const ValidateAddressComplete: HandlerType = props => {
    props.dispatch({
        type: "CustomCheckoutShipping/SetOverrideValidationResult",
        validationResult: props.apiResult,
    } as any);
    if (
        !props.apiResult ||
        !props.apiResult.isValid ||
        (props.apiResult.isValid && props.apiResult?.normalizedAddresses?.length > 0)
    ) {
        props.dispatch({
            type: "Pages/CheckoutShipping/CompleteUpdateCart",
        });
    }
};

export const chain = [BegintValidateOverrideAddress, PopulateApiParameter, RequestDataFromApi, ValidateAddressComplete];

const validateOverrideAddress = createHandlerChainRunner(chain, "ValidateOverrideAddress");
export default validateOverrideAddress;
